import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useAuth } from '../context/AuthContext.js';
import { getFirestore, doc, getDoc, collection, query, orderBy, where, getDocs, addDoc } from "firebase/firestore";
import Menu from '../components/Menu.js'; 
import "./CompanyProfile.css";

const CompanyProfile = () => {
  const { id } = useParams(); // ID de la empresa desde la URL
  const { currentUser } = useAuth();
  const [company, setCompany] = useState(null);
  const [vacantes, setVacantes] = useState([]);
  const navigate = useNavigate();
  const db = getFirestore();
  const [selectedVacante, setSelectedVacante] = useState(null);
  const [profile, setProfile] = useState(null);

  useEffect(() => {
    const fetchCompanyData = async () => {
      try {
        // Obtener datos de la empresa
        const companyRef = doc(db, "empresas", id);
        const companySnapshot = await getDoc(companyRef);
  
        if (companySnapshot.exists()) {
          setCompany({ id: companySnapshot.id, ...companySnapshot.data() });
  
          // Obtener vacantes creadas por la empresa con estado "iniciada" y fechaCierre mayor a la actual
          const vacantesRef = collection(db, "vacantes");
          const q = query(
            vacantesRef,
            where("uid", "==", companySnapshot.data().userId),
            where("estado", "==", "iniciada"),
            where("fechaCierre", ">", new Date()),
            orderBy("fechaCierre", "asc") // Opcional: ordena por la fecha de cierre
          );
  
          const vacantesSnapshot = await getDocs(q);
  
          const fetchedVacantes = vacantesSnapshot.docs.map((doc) => ({
            id: doc.id,
            ...doc.data(),
          }));
  
          setVacantes(fetchedVacantes);
        }
      } catch (error) {
        console.error("Error al cargar datos de la empresa:", error);
      }
    };
  
    fetchCompanyData();
  }, [id, db]);
  

  const calculateMatch = (vacante) => {
    if (!profile) return false;

    const weights = {
      skills: 0.5,
      ubicacion: 0.2,
      salario: 0.2,
      experiencia: 0.1,
    };

    let score = 0;

    if (profile.skills && vacante.skillsRequeridas) {
      const skillsMatch = profile.skills
        .split(",")
        .map(skill => skill.trim().toLowerCase())
        .some(skill => vacante.skillsRequeridas.includes(skill));
      if (skillsMatch) score += weights.skills;
    }

    if (profile.ubicacion && vacante.ubicacion) {
      const ubicacionMatch = profile.ubicacion.toLowerCase() === vacante.ubicacion.toLowerCase();
      if (ubicacionMatch) score += weights.ubicacion;
    }

    if (profile.salarioDeseado && vacante.salario) {
      const salarioMatch = vacante.salario >= profile.salarioDeseado;
      if (salarioMatch) score += weights.salario;
    }

    if (profile.experiencia && vacante.experienciaRequerida) {
      const experienciaMatch = parseInt(profile.experiencia, 10) >= parseInt(vacante.experienciaRequerida, 10);
      if (experienciaMatch) score += weights.experiencia;
    }

    const matchThreshold = 0.7;
    return score >= matchThreshold;
  };

    const createPostulacion = async (vacanteId) => {
      if (!currentUser) {
        alert("Debes iniciar sesión para postularte.");
        return;
      }
    
      if (profile?.role !== "postulante") { // Asegúrate de verificar el `role` desde el perfil del usuario
        alert(
          "Necesitas crear una oficina para postularse y recibir solicitudes de empleo y completar tu currículum para poder postularte."
        );
        return;
      }
    
      try {
        const postulacionData = {
          userId: currentUser.uid,
          nombre: currentUser.fullName || "Nombre no disponible",
          vacanteId,
          createdAt: new Date(),
          status: "Pendiente",
        };
    
        await addDoc(collection(db, "postulaciones"), postulacionData);
        alert("Postulación enviada con éxito.");
        closeModal();
      } catch (error) {
        console.error("Error al enviar la postulación:", error);
        alert("Hubo un error al enviar la postulación. Inténtalo de nuevo.");
      }
    };

  const handlePostulate = (vacante) => {
    if (currentUser) {
      setSelectedVacante(vacante); // Abre el modal si el usuario está autenticado
    } else {
      navigate("/login"); // Redirige al login si no está autenticado
    }
  };

  const closeModal = () => {
    setSelectedVacante(null);
  };

  const shareLinks = {
    facebook: `https://www.facebook.com/sharer/sharer.php?u=${window.location.href}`,
    linkedin: `https://www.linkedin.com/shareArticle?mini=true&url=${window.location.href}`,
    x: `https://twitter.com/intent/tweet?url=${window.location.href}`,
    whatsapp: `https://wa.me/?text=Este curriculum te puede interesar: ${window.location.href}`,
  };


  return (
    <div className="vp-container">
      <Menu />
    <div className="vp-main-content">
    <div className="company-profile-container">
      {company ? (
        <>
          <div className="company-header">
            <img src={company.logo} className="imgLogoEmpresa" alt="imagen de la empresa"  />
            <h1>{company.companyName}</h1>
            <p>{company.industry}</p>
          </div>

          <div className="company-details">
            <p><strong>Sitio Web:</strong> <a href={company.website} target="_blank" rel="noopener noreferrer">{company.sitioWeb}</a></p>
            <p><strong>Correo:</strong> {company.contactEmail}</p>
            <p><strong>Teléfono:</strong> {company.contactPhone}</p>
            <p><strong>Dirección:</strong> {company.address}, {company.city}</p>
          </div>

          <div className="company-vacantes">
            <h2>Vacantes Publicadas</h2>
            {vacantes.length > 0 ? (
              <div className="vacantesEnEmpresa">
                {vacantes.map((vacante) => (
                  <div className="vacante-card" key={vacante.id}>
                    <h3 className="vacante-title">{vacante.titulo}</h3>
                    <p className="vacante-ubicacion">
                      <strong>Ubicación:</strong> {vacante.ubicacion}
                    </p>
                    <p className="vacante-salario">
                      <strong>Salario:</strong> ${vacante.salario}
                    </p>
                    {calculateMatch(vacante) && (
                      <p className="vacante-match">✅ Coincide con tu perfil</p>
                    )}
                    <div className="iconos-refs">
                      <ul className="wrapper-icons sci">
                        <li className="icon comentarios">
                          <a href={shareLinks.facebook} target="_blank" rel="noopener noreferrer">
                            <span><i className="fa-brands fa-facebook"></i></span>
                          </a>
                        </li>
                        <li className="icon comentarios">
                          <a href={shareLinks.linkedin} target="_blank" rel="noopener noreferrer">
                            <span><i className="fa-brands fa-linkedin"></i></span>
                          </a>
                        </li>
                        <li className="icon comentarios">
                          <a href={shareLinks.x} target="_blank" rel="noopener noreferrer">
                            <span><i className="fa-brands fa-x-twitter"></i></span>
                          </a>
                        </li>
                        <li className="icon comentarios">
                          <a href={shareLinks.whatsapp} target="_blank" rel="noopener noreferrer">
                            <span><i className="fa-brands fa-whatsapp"></i></span>
                          </a>
                        </li>
                      </ul>
                    </div>
                    <button className="btn-postulate" onClick={() => handlePostulate(vacante)}>
                      Postularse
                    </button>
                  </div>
                ))}
              </div>
            ) : (
              <p>Esta empresa no tiene vacantes publicadas.</p>
            )}
          </div>

          {selectedVacante && (
            <div className="modal-overlay" onClick={closeModal}>
              <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                <h2>{selectedVacante.titulo}</h2>
                <p>{selectedVacante.descripcion}</p>
                <p>
                  <strong>Ubicación:</strong> {selectedVacante.ubicacion}
                </p>
                <p>
                  <strong>Experiencia requerida:</strong> {selectedVacante.nivelExperiencia}
                </p>
                <p>
                  <strong>Habilidades requeridas:</strong> {selectedVacante.habilidades}
                </p>
                <p>
                  <strong>Horario requerido:</strong> {selectedVacante.tipoContrato}
                </p>
                <p>
                  <strong>Salario:</strong> ${selectedVacante.salario}
                </p>
                <p>
                  <strong>Beneficios:</strong> ${selectedVacante.beneficios}
                </p>
                <p>
                  <strong>Postulate hasta:</strong> ${selectedVacante.fechaCierre}
                </p>
                <button className="btn-postulate" onClick={() => createPostulacion(selectedVacante.id)}>
                  Confirmar Postulación
                </button>
                <button className="btn btn-secondary" onClick={closeModal}>
                  Cancelar
                </button>
              </div>
            </div>
          )}

        </>
      ) : (
        <p>Cargando información de la empresa...</p>
      )}
    </div>
    </div>
  </div>
  );
  
};

export default CompanyProfile;
